import { Component, Prop, h, Build, Element } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-company-logos-type-3';

interface Logo {
  src: string;
  alt: string;
  link?: string;
}

const DEFAULT_LOGOS: Logo[] = [
  { src: 'https://marketing-static.signnow.com/366/images/home-new/logos/walmart.svg', alt: 'Walmart' },
  { src: 'https://marketing-static.signnow.com/366/images/home-new/logos/exonMobil.svg', alt: 'ExxonMobil' },
  { src: 'https://marketing-static.signnow.com/366/images/home-new/logos/apple.svg', alt: 'Apple' },
  { src: 'https://marketing-static.signnow.com/366/images/home-new/logos/comcast.svg', alt: 'Comcast' },
  { src: 'https://marketing-static.signnow.com/366/images/home-new/logos/facebook.svg', alt: 'Facebook' },
  { src: 'https://marketing-static.signnow.com/366/images/home-new/logos/FedEx.svg', alt: 'FedEx' },
];

/**
 * Company-logos band showcasing a row of customer organization logos under a centered heading.
 * Centered heading on top spans the full width; below it sits a single horizontal row of grayscale `me-logo` items wrapping naturally across breakpoints, each wrapped in Schema.org Organization microdata.
 * SignNow-branded variant within `product/signnow/company-logos` — uses the COMPANY_LOGOS_HEADING constant as fallback heading and a simple grayscale single-row layout (no carousel, marquee, or animation).
 *
 * @version 1.0.0
 * @category SignNow
 * @constant COMPANY_LOGOS_HEADING - "Over 150,000 Organizations use signNow Including a Few Names You might Recognize" - Main heading text
 * @slot heading - <me-heading variant="4" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]>
 * @example
 *   <sn-company-logos-type-3
 *     logos='[{"src":"https://marketing-static.signnow.com/366/images/home-new/logos/walmart.svg","alt":"Walmart"},{"src":"https://marketing-static.signnow.com/366/images/home-new/logos/apple.svg","alt":"Apple"}]'
 *   >
 *     <me-heading slot="heading" variant="4" tag="h2">Trusted by industry leaders</me-heading>
 *   </sn-company-logos-type-3>
 */
@Component({
  tag: 'sn-company-logos-type-3',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNCompanyLogosType3 {
  @Element() el!: HTMLElement;

  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';
  /**
   * Component constants object containing all required text constants.
   * COMPANY_LOGOS_HEADING — heading text (fallback when slot="heading" is absent).
   */
  @Prop({ mutable: true }) constants?: Record<string, string> | string;

  componentWillLoad() {
    this.constants = parseJsonProp(this.constants);
    this.logos = parseJsonProp(this.logos, DEFAULT_LOGOS);
  }

  /**
   * List of company logos to display.
   * @type {array}
   * @item.type {object}
   * @item.property {string} src - Image source URL for the logo
   * @item.property {string} alt - Image alt text for accessibility
   * @item.property {string} link - Optional URL for Schema.org metadata
   * @item.required src
   * @item.required alt
   * @minItems 6
   */
  @Prop({ mutable: true }) logos: Logo[] | string = DEFAULT_LOGOS;

  render() {
    const logosToRender = Array.isArray(this.logos) ? this.logos : [];
    if (!logosToRender.length) return null;

    // Render the heading column only when a heading is actually slotted; otherwise
    // the empty column would still carry the me-row vertical gap, leaving a margin
    // above the logos when there is no title.
    const hasHeading = !!this.el.querySelector('[slot="heading"]');

    return (
      <me-section class={COMPONENT_NAME} yGap="2xl" yGapLg="5xl" role="region" aria-label="Company Logos">
        <me-row>
          {hasHeading && (
            <me-column span="12">
              <div class={`${COMPONENT_NAME}__heading`}>
                <slot name="heading" />
              </div>
            </me-column>
          )}
          <me-column span="12">
            {!Build.isBrowser ? (
              <div class={`${COMPONENT_NAME}__list`}></div>
            ) : (
              <div class={`${COMPONENT_NAME}__list`}>
                {logosToRender.map((logo, idx) => (
                  <div class={`${COMPONENT_NAME}__list-item`} key={idx} itemScope itemType="https://schema.org/Organization">
                    <me-logo src={logo.src} alt={logo.alt} itemProp="logo" lazy={true} />
                    <meta itemProp="name" content={logo.alt} />
                    {logo.link && <meta itemProp="url" content={logo.link} />}
                  </div>
                ))}
              </div>
            )}
          </me-column>
        </me-row>
      </me-section>
    );
  }
}
